import {
  AskStrikeUpButton,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Text,
} from "components/ui";
import ProfileTwoFactorSection from "components/twofactor/ProfileTwoFactorSection";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { MdCancel, MdEdit, MdSave } from "react-icons/md";
import { authService } from "services/authService";
import { getThemeColor } from "theme/colors";

const ProfileOverview = () => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const currentUser = authService.getCurrentUser();

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword) {
      setError(t("app.validation.match"));
      return;
    }

    if (newPassword.length < 8) {
      setError(t("app.validation.minLength", { min: 8 }));
      return;
    }

    try {
      setLoading(true);
      await authService.changePassword(currentPassword, newPassword);
      setSuccess(t("app.profile.saveSuccess"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setIsEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.message || t("app.profile.saveFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSuccess("");
  };

  return (
    <Flex direction="column" gap={5} w="full">
      <Flex justify="space-between" align="center">
        <Heading
          size="2xl"
          fontWeight="bold"
          color={getThemeColor("text.primary")}
        >
          {t("app.profile.title")}
        </Heading>
        <HStack gap={3}>
          <AskStrikeUpButton context={t("app.askStrikeUp.context.profile")} />
          {!isEditing && (
            <Button
              onClick={() => setIsEditing(true)}
              colorPalette="blue"
              variant="solid"
            >
              <MdEdit size="20px" />
              {t("app.profile.editProfile")}
            </Button>
          )}
        </HStack>
      </Flex>

      {success && (
        <Box
          p={3}
          borderRadius="md"
          bg={getThemeColor("alert.success.bg")}
          border="1px solid"
          borderColor={getThemeColor("alert.success.border")}
          color={getThemeColor("alert.success.text")}
        >
          {success}
        </Box>
      )}

      <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={5}>
        {/* Account Information */}
        <GridItem>
          <Card.Root>
            <Card.Body p={6}>
              <Heading
                size="xl"
                fontWeight="bold"
                color={getThemeColor("text.primary")}
                mb={4}
              >
                {t("app.profile.accountInformation")}
              </Heading>
              <Flex direction="column" gap={4}>
                <Input
                  label={t("app.profile.email")}
                  type="email"
                  defaultValue={currentUser?.email}
                  disabled={true}
                />
                <Input
                  label={t("app.profile.role")}
                  type="text"
                  defaultValue={currentUser?.role}
                  disabled={true}
                />
                <Input
                  label={t("app.profile.companyId")}
                  type="text"
                  defaultValue={currentUser?.companyId}
                  disabled={true}
                />
              </Flex>
            </Card.Body>
          </Card.Root>
        </GridItem>

        {/* Account Status */}
        <GridItem>
          <Card.Root>
            <Card.Body p={6}>
              <Heading
                size="xl"
                fontWeight="bold"
                color={getThemeColor("text.primary")}
                mb={4}
              >
                {t("app.profile.accountStatus")}
              </Heading>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <Box
                    borderRadius="lg"
                    bg={getThemeColor("bg.secondary")}
                    p={4}
                  >
                    <Text fontSize="sm" color={getThemeColor("text.tertiary")}>
                      {t("app.profile.status")}
                    </Text>
                    <Text
                      mt={1}
                      fontSize="lg"
                      fontWeight="bold"
                      color={getThemeColor("text.primary")}
                    >
                      {t("app.profile.active")}
                    </Text>
                  </Box>
                </GridItem>
                <GridItem>
                  <Box
                    borderRadius="lg"
                    bg={getThemeColor("bg.secondary")}
                    p={4}
                  >
                    <Text fontSize="sm" color={getThemeColor("text.tertiary")}>
                      {t("app.profile.role")}
                    </Text>
                    <Text
                      mt={1}
                      fontSize="lg"
                      fontWeight="bold"
                      color={getThemeColor("text.primary")}
                    >
                      {currentUser?.role || t("app.common.user")}
                    </Text>
                  </Box>
                </GridItem>
                <GridItem colSpan={2}>
                  <Box
                    borderRadius="lg"
                    bg={getThemeColor("bg.secondary")}
                    p={4}
                  >
                    <Text fontSize="sm" color={getThemeColor("text.tertiary")}>
                      {t("app.profile.memberSince")}
                    </Text>
                    <Text
                      mt={1}
                      fontSize="lg"
                      fontWeight="bold"
                      color={getThemeColor("text.primary")}
                    >
                      {new Date().toLocaleDateString()}
                    </Text>
                  </Box>
                </GridItem>
              </Grid>
            </Card.Body>
          </Card.Root>
        </GridItem>
      </Grid>

      {/* Change Password Section */}
      {isEditing && (
        <Card.Root>
          <Card.Body p={6}>
            <Heading
              size="xl"
              fontWeight="bold"
              color={getThemeColor("text.primary")}
              mb={4}
            >
              {t("app.profile.changePassword")}
            </Heading>
            <form onSubmit={handleSave}>
              {error && (
                <Box
                  mb={4}
                  p={3}
                  borderRadius="md"
                  bg={getThemeColor("alert.error.bg")}
                  border="1px solid"
                  borderColor={getThemeColor("alert.error.border")}
                  color={getThemeColor("alert.error.text")}
                >
                  {error}
                </Box>
              )}
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gap={4}
              >
                <GridItem>
                  <Input
                    label={t("app.profile.currentPassword")}
                    type="password"
                    placeholder={t("app.profile.currentPasswordPlaceholder")}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </GridItem>
                <GridItem>
                  <Input
                    label={t("app.profile.newPassword")}
                    type="password"
                    placeholder={t("app.profile.newPasswordPlaceholder")}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </GridItem>
                <GridItem>
                  <Input
                    label={t("app.profile.confirmPassword")}
                    type="password"
                    placeholder={t("app.profile.confirmPasswordPlaceholder")}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </GridItem>
              </Grid>

              <Flex gap={3} mt={6}>
                <Button
                  type="button"
                  onClick={handleCancel}
                  variant="subtle"
                  colorPalette="gray"
                  disabled={loading}
                >
                  <MdCancel size="20px" />
                  {t("app.profile.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  colorPalette="blue"
                  loading={loading}
                >
                  <MdSave size="20px" />
                  {t("app.profile.saveChanges")}
                </Button>
              </Flex>
            </form>
          </Card.Body>
        </Card.Root>
      )}

      {/* Security — two-factor authentication */}
      <ProfileTwoFactorSection />
    </Flex>
  );
};

export default ProfileOverview;
